import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
    AccountingDataRequest,
    BasketItem,
    BasketItemRequest,
    ClassificationDataRequest,
    MaterialGroupDataRequest,
    DeleteAttachmentBasketItemFileRequest,
    UpdateArticleNumberDataRequest,
    UpdateDesiredDeliveryDateDataRequest,
    UpdateInternalPositionNoteParameters,
    UpdateSiteParameters,
    CompanySetting,
    IPurchasingGroupResolution,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';
import { BasketSupplierItemStore } from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/data-access';
import { Observable } from 'rxjs';

@Component({
    selector: 'basket-supplier-item',
    templateUrl: './basket-supplier-item.component.html',
    styleUrl: './basket-supplier-item.component.scss',
    providers: [BasketSupplierItemStore],
    standalone: false
})
export class BasketSupplierItemComponent implements OnInit, OnChanges {
    basketSupplierItemStore = inject(BasketSupplierItemStore);

    @Input() supplierBasketItem: BasketItem;
    @Input() parentSBId!: string;
    @Input() isEditableStep: boolean;
    @Input() companySetting!: CompanySetting;
    @Input() companyCodeId: string | null = null;
    @Input() resolvedPurchasingGroups: readonly IPurchasingGroupResolution[] = [];
    @Input() purchasingGroupVisible = true;

    @Output() basketItemDeleted = new EventEmitter<BasketItemRequest>();
    @Output() productNumberValidationMessageShowed = new EventEmitter<boolean>();
    @Output() desiredDeliveryDateValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() accountingValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() classificationValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() materialGroupValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() siteValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();

    supplierItem$: Observable<BasketItem | null>;

    ngOnInit(): void {
        this.streamSupplierItem();
        this.basketSupplierItemStore.getMaxUploadSize$();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['supplierBasketItem'] && this.supplierBasketItem) {
            this.loadSupplierItem(this.supplierBasketItem.id);
        }
    }

    changeAccounting(value: AccountingDataRequest) {
        if (value.accountingChangeData.value !== undefined) {
            this.basketSupplierItemStore.saveAccountingValue$(value);
        } else {
            this.basketSupplierItemStore.deleteAccountingValue$(value);
        }
    }

    changeClassification(value: ClassificationDataRequest) {
        if (value.classificationChangeData?.irdicc) {
            this.basketSupplierItemStore.saveClassificationValue$(value);
        } else {
            this.basketSupplierItemStore.deleteClassificationValue$(value);
        }
    }

    changeMaterialGroup(value: MaterialGroupDataRequest) {
        if (value.materialGroupChangeData?.code) {
            this.basketSupplierItemStore.saveMaterialGroupValue$(value);
        } else {
            this.basketSupplierItemStore.deleteMaterialGroupValue$(value);
        }
    }

    deleteBasketItemAttachmentFile(attachmentFile: DeleteAttachmentBasketItemFileRequest) {
        this.basketSupplierItemStore.deleteAttachmentBasketItemFile$(attachmentFile);
    }

    updateProductNumber(updateArticleNumberDataRequest: UpdateArticleNumberDataRequest) {
        this.basketSupplierItemStore.updateProductNumber$(updateArticleNumberDataRequest);
    }

    updateDesiredDeliveryDate(desiredDeliveryDateInfoSB: UpdateDesiredDeliveryDateDataRequest) {
        this.basketSupplierItemStore.updateDesiredDeliveryDate$(desiredDeliveryDateInfoSB);
    }

    openProductDialog(productId: string) {
        this.basketSupplierItemStore.openProductDialog$(productId);
    }

    sendInternalPositionNote(positionNote: UpdateInternalPositionNoteParameters) {
        this.basketSupplierItemStore.updateInternalPositionNote$(positionNote);
    }

    // EPROC-10907: persist the customer site (WERKS) selected on a basket position
    changeSite(params: UpdateSiteParameters) {
        this.basketSupplierItemStore.updateSite$(params);
    }

    openAttachmentBasketItemDialog(item: BasketItemRequest) {
        this.basketSupplierItemStore.openUploadFileBasketItemDialog$(item);
    }

    deleteBasketItem(deleteBasketItemData: BasketItemRequest) {
        this.basketItemDeleted.emit(deleteBasketItemData);
    }

    onShowProductNumberValidationMessage(isShowMessage: boolean) {
        this.productNumberValidationMessageShowed.emit(isShowMessage);
    }

    onDesiredDeliveryDateValidated(event: { valid: boolean; basketItemId: number }) {
        this.desiredDeliveryDateValidated.emit(event);
    }

    updateBasketQuantity(item: BasketItemRequest) {
        this.basketSupplierItemStore.updateBasketAmount$(item);
    }

    onAccountingValidated(event: { valid: boolean; basketItemId: number }) {
        this.accountingValidated.emit(event);
    }

    onSiteValidated(event: { valid: boolean; basketItemId: number }) {
        this.siteValidated.emit(event);
    }

    onClassificationValidated(event: { valid: boolean; basketItemId: number }) {
        this.classificationValidated.emit(event);
    }

    onMaterialGroupValidated(event: { valid: boolean; basketItemId: number }) {
        this.materialGroupValidated.emit(event);
    }

    private loadSupplierItem(itemId: number): void {
        this.basketSupplierItemStore.loadSupplierItem$(itemId);
    }

    private streamSupplierItem(): void {
        this.supplierItem$ = this.basketSupplierItemStore.supplierBasketItem;
    }
}
